<script>
import { Bar, mixins } from 'vue-chartjs'
import './ChartBarCustom'
import numeral from 'numeral'
import moment from 'moment'

export default {
  extends: Bar,
  mixins: [
    mixins.reactiveProp
  ],
  props: {
    xAxisLabel: {
      type: String,
      required: false,
      default: ''
    },
    xAxisType: {
      type: String,
      required: false,
      default: 'category'
    },
    showLegend: {
      type: Boolean,
      required: false,
      default: false
    },
    currency: {
      type: String,
      required: false,
      default: ''
    }
  },
  data () {
    const self = this
    return {
      gradient: null,
      options: {
        responsive: true,
        maintainAspectRatio: false,
        layout: {
          padding: {
            left: 30,
            right: 20
          }
        },
        hover: {
          mode: 'index',
          intersect: false
        },
        legend: {
          display: this.showLegend,
          position: 'top',
          align: 'end',
          labels: {
            usePointStyle: true,
            boxWidth: 8,
            padding: 20,
            fontColor: '#63627C',
            fontStyle: 'bold'
          }
        },
        tooltips: {
          mode: 'index',
          intersect: false,
          backgroundColor: '#FFFFFF',
          titleFontColor: '#63627C',
          titleFontSize: 13,
          bodyFontColor: '#63627C',
          callbacks: {
            title: (tooltipItems, data) => {
              const date = tooltipItems[0].xLabel
              if (this.xAxisType === 'date' && moment(date, 'YYYY-MM-DD', true).isValid()) {
                return moment(date).format('DD MMMM YYYY')
              }
              return null
            },
            label: function (tooltipItem, data) {
              const dataset = data.datasets[tooltipItem.datasetIndex]
              const datasetLabel = dataset.label || ''
              const value = dataset.type === 'line' && self.currency
                ? self.$fmt.currency(self.currency, tooltipItem.yLabel)
                : self.$fmt.integer(tooltipItem.yLabel)
              return `${datasetLabel}: ${value}`
            }
          }
        },
        scales: {
          yAxes: [{
            id: 'left-y-axis',
            position: 'left',
            scaleLabel: {
              display: false,
              labelString: 'Q de tickets',
              fontColor: '#63627C',
              fontSize: 11,
              fontStyle: 'bold'
            },
            gridLines: {
              display: false
            },
            ticks: {
              suggestedMin: 0,
              fontColor: this.$vuetify.theme.dark ? '#E6E9F0' : '#8B94A9',
              fontSize: 11,
              maxTicksLimit: 6,
              callback: function (value) {
                return numeral(value).format('0a').toUpperCase().replace('.0', '')
              }
            }
          }, {
            id: 'right-y-axis',
            position: 'right',
            scaleLabel: {
              display: false,
              labelString: 'Promedio de ventas',
              fontColor: '#63627C',
              fontSize: 11,
              fontStyle: 'bold'
            },
            gridLines: {
              display: true,
              color: '#CFD8DC',
              drawBorder: false
            },
            ticks: {
              suggestedMin: 0,
              fontColor: this.$vuetify.theme.dark ? '#E6E9F0' : '#8B94A9',
              fontSize: 11,
              maxTicksLimit: 6,
              callback: function (value) {
                return numeral(value).format('0.0a').toUpperCase().replace('.0', '')
              }
            }
          }],
          xAxes: [{
            barPercentage: 0.7,
            categoryPercentage: 0.7,
            scaleLabel: {
              display: !!this.xAxisLabel,
              labelString: this.xAxisLabel,
              fontColor: '#63627C',
              fontSize: 11,
              fontStyle: 'bold'
            },
            gridLines: {
              display: false
            },
            ticks: {
              fontColor: this.$vuetify.theme.dark ? '#E6E9F0' : '#8B94A9',
              fontSize: 11,
              callback: (value, index, values) => {
                if (this.xAxisType === 'date') {
                  return moment(value).format('DD')
                }
                return value
              }
            }
          }]
        }
      }
    }
  },
  mounted () {
    const ctx = this.$refs.canvas.getContext('2d')
    const gradient = ctx.createLinearGradient(0, 0, 0, 400)
    gradient.addColorStop(0, 'rgba(0, 84, 212, 0.25)')
    gradient.addColorStop(1, 'rgba(255, 255, 255, 0)')
    this.gradient = gradient
    this.addPlugin({
      id: 'vertical-y-axis-labels',
      afterDraw: (chart) => {
        const ctx = chart.ctx

        const leftYAxis = chart.scales['left-y-axis']
        if (leftYAxis) {
          ctx.save()
          const x = leftYAxis.left - 10
          const y = leftYAxis.top + (leftYAxis.height / 2)
          ctx.translate(x, y)
          ctx.rotate(-Math.PI / 2)
          ctx.textAlign = 'center'
          ctx.textBaseline = 'bottom'
          ctx.font = `600 12px ${getComputedStyle(this.$refs.canvas).fontFamily || 'sans-serif'}`
          ctx.fillStyle = '#63627C'
          ctx.fillText('Q de tickets', 0, 0)
          ctx.restore()
        }

        const rightYAxis = chart.scales['right-y-axis']
        if (rightYAxis) {
          ctx.save()
          const x = rightYAxis.right + 20
          const y = rightYAxis.top + (rightYAxis.height / 2)
          ctx.translate(x, y)
          ctx.rotate(-Math.PI / 2)
          ctx.textAlign = 'center'
          ctx.textBaseline = 'bottom'
          ctx.font = `600 12px ${getComputedStyle(this.$refs.canvas).fontFamily || 'sans-serif'}`
          ctx.fillStyle = '#63627C'
          ctx.fillText('Promedio de ventas', 0, 0)
          ctx.restore()
        }
      }
    })
    this.drawChart()
  },
  watch: {
    chartData () {
      this.drawChart()
    }
  },
  methods: {
    drawDatasets () {
      if (this.chartData && this.chartData.datasets) {
        this.chartData.datasets.forEach((dataset) => {
          if (dataset.type === 'line') {
            dataset.lineTension = 0.4
            dataset.fill = true
            dataset.backgroundColor = this.gradient
            dataset.pointRadius = 0
            dataset.pointHoverRadius = 5
            dataset.pointBackgroundColor = dataset.borderColor
            dataset.order = 1
          } else {
            dataset.barThickness = 12
            dataset.order = 2
          }
        })
      }
    },
    drawChart () {
      this.drawDatasets()
      this.renderChart(this.chartData, this.options)
      this.$emit('legend', this.$data._chart.generateLegend())
    }
  }
}
</script>